import { useApiUrl, useTranslate, useNavigation } from '@pankod/refine-core';
import {
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Table,
  Typography,
  Checkbox,
  TimePicker,
  DatePicker,
} from 'antd';
import {
  TagField,
  DateField,
  TextField,
  Space,
  CreateButton,
  EditButton,
  DeleteButton,
  /*ShowButton,*/
} from '@pankod/refine-antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  SaveOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import UserHandler from 'util/UserHandler';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  required: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number' | 'text' | 'bool' | 'date' | 'time';
  record: any;
  index: number;
  children: React.ReactNode;
}

export interface CustomTableProps {
  columns: any[];
  dataSource: any[];
  config: {
    refresh?: number;
    table?: string;
    defaultPropsValues?: any;
    key: string;
    addNew?: any;
    edit?: any;
    delete?: any;
  };
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  required,
  record,
  index,
  children,
  ...restProps
}) => {
  let inputNode;
  let getValueProps;
  let valuePropName;
  switch (inputType) {
    case 'number':
      inputNode = <InputNumber />;
      break;
    case 'bool':
      inputNode = <Checkbox />;
      valuePropName = 'checked';
      break;
    case 'date':
      inputNode = <DatePicker />;
      getValueProps = (value: any) => ({ value: value ? dayjs(value) : '' });
      break;
    case 'time':
      inputNode = <TimePicker />;
      getValueProps = (value: any) => ({ value: value ? dayjs(value) : '' });
      break;
    default:
      inputNode = <Input />;
      break;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          valuePropName={valuePropName}
          getValueProps={getValueProps}
          rules={[
            {
              required: required,
              message: `Please Input ${title}!`,
            },
          ]}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  dataSource,
  config,
}) => {
  const [form] = Form.useForm();
  const [data, setData] = useState(dataSource);
  const [editingKey, setEditingKey] = useState('');
  const apiUrl = useApiUrl();
  const t = useTranslate();
  const { edit: editPage } = useNavigation();

  React.useEffect(() => {
    const newData = [...dataSource];
    setData(newData);
  }, [config.refresh]);

  const isEditing = (record: any) => record[config.key] === editingKey;

  const edit = (record: Partial<any> & { key: React.Key }) => {
    if (config.edit?.type === 'redirect') {
      config.edit?.action(record[config.key]);
    } else if (
      typeof config.edit === 'function' &&
      !columns.find(c => c.editable)
    ) {
      config.edit(record);
    } else {
      form.setFieldsValue({ ...record });
      setEditingKey(record[config.key]);
    }
  };

  const cancel = () => {
    setEditingKey('');
  };

 const addNew = () => {
  if (typeof config.addNew === 'function') {
    config.addNew();
  } else {
    const newData = [...data];
    const item = {
      ...config.addNew.default, // Contient user_id
      name: undefined,
      district: undefined,
      address: undefined,
      civic_number: undefined,
      city_code: undefined,
      latitude: undefined,
      longitude: undefined,
      extra_info: undefined,
    };
    item[config.key] = 'new_' + newData.length;
    newData.push(item);
    setData(newData);
    form.resetFields(); // Réinitialiser le formulaire pour vider les champs
    form.setFieldsValue(item); // Définir les valeurs (seulement user_id et id)
    setEditingKey(item[config.key]);
  }
};

const save = async (key: React.Key) => {
  try {
   
    const row = (await form.validateFields()) as any;
    const newData = [...data];
    const index = newData.findIndex(item => key === item[config.key]);

    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });

      // Construire formData avec les champs pertinents
      const formData = {
        name: row.name || undefined,
        district: row.district,
        address: row.address,
        civic_number: row.civic_number,
        city_code: row.city_code,
        latitude: row.latitude ? parseFloat(row.latitude) : undefined,
        longitude: row.longitude ? parseFloat(row.longitude) : undefined,
        extra_info: row.extra_info || undefined,
        user_id: parseInt(config.defaultPropsValues.user_id, 10),
      };

      // console.log('Données envoyées à l\'API:', JSON.stringify(formData, null, 2));

      if (key.toString().includes('new_')) {
        // Nouvelle adresse : requête POST
        const response = await axios
          .post(`${apiUrl}/${config.addNew?.endpoint || config.edit}`, formData, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${UserHandler.getInstance().token}`,
            },
          })
          .then(res => res.data)
          .catch(error => {
            console.error('Erreur API:', error.response?.data);
            throw new Error(`Erreur API: ${error.response?.data?.message || 'Erreur inconnue'}`);
          });

        console.log('Réponse de l\'API:', response);

        if (response?.data?.id) {
          newData[index] = { ...newData[index], id: response.data.id };
          setData(newData);
          setEditingKey('');
        } else {
          throw new Error('Échec de la création de l\'adresse');
        }
      } else {
        // Mise à jour : requête PATCH
        const response = await axios
          .patch(`${apiUrl}/${config.edit}/${key}`, formData, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${UserHandler.getInstance().token}`,
            },
          })
          .then(res => res.data)
          .catch(error => {
            console.error('Erreur API:', error.response?.data);
            throw new Error(`Erreur API: ${error.response?.data?.message || 'Erreur inconnue'}`);
          });

        setData(newData);
        setEditingKey('');
      }
    } else {
      console.warn('Enregistrement non trouvé pour la clé:', key);
      setEditingKey('');
    }
  } catch (errInfo) {
    console.error('Erreur lors de la sauvegarde:', errInfo);
   
  }
};

async function deleteRecord(id: number) {
    try {
        // Vérifier si l'enregistrement est nouveau (pas encore en base de données)
        const isUnsavedRecord = id.toString().includes('new_');
        
        if (isUnsavedRecord) {
            // Pour les nouveaux enregistrements : simplement supprimer du tableau
            setData(prevData => prevData.filter(item => item[config.key] !== id));
            
        } else {
            // Pour les enregistrements existants : supprimer en base de données
            const response = await axios.delete(`${apiUrl}/user/address/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${UserHandler.getInstance().token}`,
                },
            });
            
            setData(prevData => prevData.filter(item => item[config.key] !== id));
           
        }
    } catch (error: any) {
        console.error('Erreur API complète:', error.response);
       
        throw new Error(`Erreur API: ${error.response?.data?.message || 'Erreur inconnue'} (Statut: ${error.response?.status || 'Inconnu'})`);
    }
}

  columns = generateTableColumns(columns);

  if (config.edit || config.delete) {
    const azioni: any = {
      title: t('actions'),
      width: 90,
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Typography.Link
              onClick={() => save(record[config.key])}
              style={{ marginRight: 8 }}>
              <button
                type="button"
                className="ant-btn ant-btn-default ant-btn-sm ant-btn-icon-only">
                <SaveOutlined />
              </button>
            </Typography.Link>
            <Popconfirm
              title="Sicuro di voler abbandonare la modifica?"
              onConfirm={cancel}>
              <button
                type="button"
                className="ant-btn ant-btn-default ant-btn-sm ant-btn-icon-only ant-btn-dangerous">
                <RedoOutlined />
              </button>
            </Popconfirm>
          </Space>
        ) : (
          <Space>
            {config.edit && (
              <EditButton hideText size="small" onClick={() => edit(record)} />
            )}
            {config.delete && (
              <Popconfirm
                title="Sicuro di voler eliminare il dato?"
                onConfirm={() => deleteRecord(record[config.key])}>
                <button
                  type="button"
                  className="ant-btn ant-btn-default ant-btn-sm ant-btn-icon-only ant-btn-dangerous">
                  <DeleteOutlined />
                </button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    };
    columns.push(azioni);
  }

  const mergedColumns = columns.map(col => {
    col.responsive = ['md'];
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        required: col.required,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  let scroll: any = {
    scroll: { y: 'calc(100vh - 300px)' }
  };
  let pagination: any = {
    onChange: cancel,
  };
  if (config.addNew) {
    scroll = {
      y: 450,
      x: true,
      scrollToFirstRowOnChange: true,
    };
    pagination = false;
  }

  return (
    <Form form={form} component={false}>
      {config.addNew && (
        <CreateButton
          style={{ float: 'right', marginBottom: '8px' }}
          onClick={addNew}>
          Crea
        </CreateButton>
      )}
      <Table
        rowKey={config.key}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        dataSource={data}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={pagination}
        scroll={{ y: 'calc(100vh - 300px)' }}
      />
    </Form>
  );
};

export const generateTableColumns = (
  fields: {
    name: string;
    type: string;
    title: string;
    editable: boolean;
    required: boolean;
    edit?: any;
    render?: (value: any, record?: any, index?: number) => React.ReactNode; // Ajouter render comme prop optionnelle
  }[],
) => {
  const columns: any = [];
  fields.forEach(field => {
    const col: any = {
      title: field.title,
      dataIndex: field.name,
      editable: field.editable,
      required: field.required,
      // Utiliser la fonction render personnalisée si elle existe, sinon définir une fonction par défaut
      render: field.render
        ? field.render
        : (value: any) => {
            switch (field.type) {
              case 'tag':
                return <TagField value={value} />;
              case 'number':
                return <TagField value={Math.round(value * 100) / 100} />;
              case 'bool':
              case 'boolean':
                return value ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
              case 'time':
              case 'date':
                return (
                  <DateField
                    format={field.type === 'time' ? 'LTS' : 'DD/MM/YYYY'}
                    value={value}
                  />
                );
              case 'actions_edit':
                return (
                  <EditButton
                    hideText
                    size="small"
                    recordItemId={value}
                    onClick={() => field.edit?.(value)}
                  />
                );
              default:
                return <TextField value={value} />;
            }
          },
    };
    if (field.type.includes('actions')) {
      col.align = 'center';
      col.width = 120;
    }
    if (field.type === 'bool') {
      col.width = 80;
      if (field.name.length > 10) col.width = (80 * field.name.length) / 10;
      col.align = 'center';
    }
    columns.push(col);
  });
  return columns;
};
         












